import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { EditorProps } from "@hightouch/formkit";
import { Controller, useWatch } from "react-hook-form";
import {
  Box,
  Button,
  Column,
  Row,
  Spinner,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";
import { EditorView } from "@codemirror/view";
import { Editor, type Language } from "src/components/editor";
import {
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import { getLiquidColumnReferences } from "src/formkit/components/mapper-v2/utils";
import { renderLiquidPreview } from "src/utils/render-liquid-preview";
import type { SharedProps } from ".";

export const FKEditor = (props: EditorProps & SharedProps) => {
  if (props.enablePreview) {
    return (
      <TemplatePreview
        name={props.name}
        language={props.language}
        languageAsLiquidBase={props.languageAsLiquidBase}
        lineWrapping={props.lineWrapping}
        characterCount={props.characterCount}
      />
    );
  }
  return <EditableEditor {...props} />;
};

const EditableEditor = ({
  name,
  language,
  languageAsLiquidBase,
  lineWrapping,
  columnSidebar,
  characterCount,
  placeholder,
  isDisabled,
  error,
}: EditorProps & SharedProps) => {
  const languageLabel = languageAsLiquidBase ? "liquid" : language?.toString();

  const viewRef = useRef<EditorView>();
  const captureView = useCallback(({ view }: { view: EditorView }) => {
    viewRef.current = view;
  }, []);
  // Replaces the selection, or inserts at the caret, then hands focus back so
  // typing continues right after the inserted tag.
  const insertAtCursor = useCallback((text: string) => {
    const view = viewRef.current;
    if (!view) {
      return;
    }
    view.dispatch(view.state.replaceSelection(text));
    view.focus();
  }, []);

  return (
    <Controller
      name={name}
      render={({ field }) => {
        const text = typeof field.value === "string" ? field.value : "";
        const editor = (
          <Editor
            minHeight="200px"
            readOnly={isDisabled}
            value={text}
            language={language}
            languageAsLiquidBase={languageAsLiquidBase}
            placeholder={placeholder?.toString()}
            onChange={field.onChange}
            onInit={captureView}
            extensions={lineWrapping ? [EditorView.lineWrapping] : undefined}
          />
        );
        return (
          <EditorShell
            label={languageLabel}
            isInvalid={Boolean(error)}
            trailing={characterCount ? <CharacterCount text={text} /> : null}
          >
            {columnSidebar ? (
              <Row flex={1} minHeight={0}>
                <Column flex={1} minWidth={0}>
                  {editor}
                </Column>
                <ColumnSidebar
                  isDisabled={isDisabled}
                  onInsert={insertAtCursor}
                />
              </Row>
            ) : (
              editor
            )}
          </EditorShell>
        );
      }}
    />
  );
};

type ColumnSidebarProps = {
  isDisabled: boolean;
  onInsert: (reference: string) => void;
};

const ColumnSidebar: FC<ColumnSidebarProps> = ({ isDisabled, onInsert }) => {
  const { columns } = useFormkitContext();
  const references = useMemo(
    () => getLiquidColumnReferences(columns ?? []),
    [columns],
  );

  return (
    <Column
      as="aside"
      aria-label="Model columns"
      width="3xs"
      flexShrink={0}
      minHeight={0}
      overflowY="auto"
      borderLeftWidth="hairline"
      borderLeftStyle="solid"
      borderColor="base.border"
      bg="base.lightBackground"
    >
      <Box
        position="sticky"
        top={0}
        px={3}
        py={2}
        bg="base.lightBackground"
        borderBottomWidth="hairline"
        borderBottomStyle="solid"
        borderColor="base.border"
      >
        <Text size="sm" fontWeight="medium" color="text.secondary">
          Columns
        </Text>
      </Box>
      {references.length === 0 ? (
        <Box px={3} py={2}>
          <Text size="sm" color="text.secondary">
            No columns to show
          </Text>
        </Box>
      ) : (
        <Box as="ul" m={0} p={0} sx={{ listStyle: "none" }}>
          {references.map((column) => (
            <Box as="li" key={column.reference}>
              <Box
                as="button"
                type="button"
                display="flex"
                alignItems="center"
                gap={2}
                width="100%"
                px={3}
                py={1.5}
                textAlign="left"
                outline="none"
                cursor={isDisabled ? "not-allowed" : "pointer"}
                _hover={{ bg: isDisabled ? undefined : "base.background" }}
                _focusVisible={{ boxShadow: "outline" }}
                disabled={isDisabled}
                onClick={() => onInsert(column.reference)}
              >
                <Box flex={1} minWidth={0}>
                  <TextWithTooltip size="sm" message={column.reference}>
                    {column.label}
                  </TextWithTooltip>
                </Box>
                {column.type && (
                  <Box flexShrink={0}>
                    <Text size="sm" color="text.tertiary">
                      {column.type.toLowerCase()}
                    </Text>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Column>
  );
};

type TemplatePreviewProps = {
  name: string;
  language?: Language;
  languageAsLiquidBase?: boolean;
  lineWrapping?: boolean;
  characterCount?: boolean;
};

const TemplatePreview: FC<TemplatePreviewProps> = ({
  name,
  language,
  lineWrapping,
  characterCount,
}) => {
  const template = (useWatch({ name }) as string | undefined) ?? "";
  const { rows, loadingRows, reloadRows, queryRowsError } =
    useFormkitDataContext();

  // TODO(eric.lovett): This `biome-ignore` was part of a blanket suppression so that we could
  // turn on the rule. It is probably covering up a real bug. Please fix this or replace this
  // TODO with an explanation of why we don't want all deps listed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy suppression, probably a bug
  useEffect(() => {
    if (!rows?.length && !loadingRows) {
      reloadRows();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { output, error } = useMemo(
    () =>
      renderLiquidPreview(
        template,
        {
          row: (rows?.[0] ?? {}) as Record<string, unknown>,
          rows: (rows ?? []) as Record<string, unknown>[],
        },
        language,
      ),
    [template, rows, language],
  );

  const rendered =
    !queryRowsError && !loadingRows && rows?.length && !error
      ? (output ?? "")
      : undefined;

  return (
    <EditorShell
      label={language?.toString()}
      trailing={
        characterCount && rendered !== undefined ? (
          <CharacterCount text={rendered} />
        ) : null
      }
    >
      {queryRowsError ? (
        <PreviewMessage variant="danger">{queryRowsError}</PreviewMessage>
      ) : loadingRows ? (
        <Row align="center" gap={2} p={4}>
          <Spinner size="sm" />
          <Text color="text.secondary">Loading sample data from model…</Text>
        </Row>
      ) : !rows?.length ? (
        <Row align="center" justify="space-between" gap={4} p={4}>
          <Text color="text.secondary">No sample data available.</Text>
          <Button size="sm" variant="secondary" onClick={() => reloadRows()}>
            Load preview
          </Button>
        </Row>
      ) : error ? (
        <PreviewMessage variant="danger">{error}</PreviewMessage>
      ) : (
        <Editor
          minHeight="200px"
          readOnly
          value={output ?? ""}
          language={language}
          extensions={lineWrapping ? [EditorView.lineWrapping] : undefined}
        />
      )}
    </EditorShell>
  );
};

// Counts code points so a visible symbol such as an emoji counts once.
const CharacterCount: FC<{ text: string }> = ({ text }) => {
  const count = Array.from(text).length;
  return (
    <Text size="sm" color="text.secondary">
      {count.toLocaleString()} {count === 1 ? "character" : "characters"}
    </Text>
  );
};

type EditorShellProps = {
  label: ReactNode;
  isInvalid?: boolean;
  trailing?: ReactNode;
  children: ReactNode;
};

const EditorShell: FC<EditorShellProps> = ({
  label,
  isInvalid,
  trailing,
  children,
}) => (
  <Column
    borderWidth="hairline"
    borderStyle="solid"
    borderColor={isInvalid ? "danger.base" : "base.border"}
    borderRadius="md"
    maxHeight="300px"
    minHeight="200px"
    overflow="hidden"
  >
    <Row
      align="center"
      p={4}
      borderBottomWidth="hairline"
      borderBottomStyle="solid"
      borderColor="base.border"
      gap={4}
      justify="space-between"
      flexShrink={0}
    >
      <Text fontWeight="medium">{label}</Text>
      {trailing}
    </Row>
    {children}
  </Column>
);

const PreviewMessage: FC<{ variant: "danger"; children: ReactNode }> = ({
  children,
}) => (
  <Box
    m={4}
    px={3}
    py={2}
    bg="danger.background"
    borderRadius="md"
    border="1px solid"
    borderColor="danger.border"
  >
    <Box fontFamily="mono" fontSize="sm" color="danger.base">
      {children}
    </Box>
  </Box>
);
